import type { NodeId } from '../../lib/types';
import type { SliceCreator } from '../state';

/**
 * Measured node boxes — the input the pure layout takes instead of measuring for itself.
 *
 * THIS SLICE IS DELIBERATELY THIN, AND SAYS SO. It has no producer yet: the only thing that
 * can fill it is the `ResizeObserver` that arrives with `MapStage`, and the only thing that
 * reads it is `lib/layout/layout.ts`, whose consumer is `MapNode`. Both are the next stage.
 * What exists here is the shape those two have to agree on, and the identity discipline that
 * makes a per-node subscription work — building more would be inventing usage.
 *
 * WHY IT IS NOT OPTIONAL EITHER. The layout circularity CLAUDE.md describes is that node
 * height is CSS-derived from text wrapping, so `autoLayout()` renders, measures, then writes
 * measured heights back into the model. The escape is to make measurement an INPUT: observe,
 * store here, and let `layout(graph, sizes, opts)` stay pure. `lib/docnav.ts` already proved
 * the shape at a smaller size with the doc-navigation band.
 */
export interface NodeSize {
  w: number;
  h: number;
}

export type SizeMap = Record<NodeId, NodeSize>;

export interface SizesSlice {
  sizes: SizeMap;

  /**
   * Record one node's measured box.
   *
   * A no-op when the numbers are unchanged, which is the common case: a `ResizeObserver`
   * fires for reasons that do not change a box, and re-rendering the map for each of them
   * would undo the point of measuring outside layout.
   */
  setSize: (id: NodeId, size: NodeSize) => void;

  /** A batch from one observer callback — several entries, one notification. */
  setSizes: (next: SizeMap) => void;

  /**
   * The whole map, replacing what was there — a measurement sweep, not a merge.
   *
   * The DIFFERENCE MATTERS AND IS NOT COSMETIC. A node that is collapsed away is not in the
   * DOM, and the vanilla `hOf()` answers `ROOTH` for anything it cannot find. Merging would
   * keep the last height a node had before it was hidden and hand the layout a different
   * number from the one the original used, which moves every sibling below it. So the sweep
   * says what IS measured, and anything absent falls back exactly as it did.
   */
  replaceSizes: (next: SizeMap) => void;

  /** A node that has gone. Leaving it would keep it in the layout's input for ever. */
  clearSize: (id: NodeId) => void;
}

const sameSize = (a: NodeSize | undefined, b: NodeSize): boolean =>
  !!a && a.w === b.w && a.h === b.h;

export const createSizesSlice: SliceCreator<SizesSlice> = (set, get) => ({
  sizes: {},

  setSize: (id, size) => {
    const prev = get().sizes;
    if (sameSize(prev[id], size)) return;
    set({ sizes: { ...prev, [id]: size } });
  },

  setSizes: (next) => {
    const prev = get().sizes;
    let changed = false;
    for (const id in next) {
      if (!sameSize(prev[id], next[id])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    const merged: SizeMap = { ...prev };
    for (const id in next) {
      merged[id] = sameSize(prev[id], next[id]) ? prev[id] : next[id];
    }
    set({ sizes: merged });
  },

  replaceSizes: (next) => {
    const prev = get().sizes;
    const merged: SizeMap = {};
    let changed = false;
    for (const id in next) {
      // Identity is preserved per entry, so a sweep in which one node grew does not hand
      // every other node a new object and re-render all of them.
      if (sameSize(prev[id], next[id])) merged[id] = prev[id];
      else {
        merged[id] = next[id];
        changed = true;
      }
    }
    if (!changed) {
      for (const id in prev) {
        if (!(id in next)) {
          changed = true;
          break;
        }
      }
    }
    if (changed) set({ sizes: merged });
  },

  clearSize: (id) => {
    const prev = get().sizes;
    if (!(id in prev)) return;
    const merged = { ...prev };
    delete merged[id];
    set({ sizes: merged });
  },
});
